import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getAllFieldsTouched } from '../../../../../../common';
import {
  applyGeneralInfoFieldOverrides,
  getChangedGeneralInfoFieldNames,
  getChangedSavedFieldValues,
  getMergedPatchData,
  getSavedFieldValues,
  getValuesWithFieldOverride,
  isDateFieldValid,
  isFieldSchemaValid,
} from './utils';
import { onboardingQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch } from '../../../../../../../store';
import type {
  GeneralInformationFieldName,
  GeneralInformationFieldOverride,
  GeneralInformationFieldValue,
  GeneralInformationSavedFieldValues,
} from './types';
import type { GeneralFormValues } from '../../../../../common';
import type { FormikProps } from 'formik';

export default function useSaveGeneralInformationFieldOnBlur(
  formik: FormikProps<GeneralFormValues>,
  initialValues: GeneralFormValues,
) {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const savedFieldValuesRef = useRef<GeneralInformationSavedFieldValues>(
    getSavedFieldValues(initialValues),
  );
  const savingPromiseRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = getSavedFieldValues(initialValues);
  }, [initialValues]);

  const performGeneralInfoSave = useCallback(
    async (values: GeneralFormValues) => {
      if (!onboardingQuestionnaireId) {
        return;
      }

      const previousSave = savingPromiseRef.current;
      const currentSave = (async () => {
        try {
          await previousSave;
        } catch {
          // Swallow prior save errors so this save still proceeds.
        }

        const changedSavedValues = getChangedSavedFieldValues(
          values,
          savedFieldValuesRef.current,
        );

        if (Object.keys(changedSavedValues).length === 0) {
          return;
        }

        const fieldNames = getChangedGeneralInfoFieldNames(changedSavedValues);
        const resultAction = await dispatch(
          onboardingQuestionnaireActions.updateOnboardingQuestionnaireGeneralInfo({
            onboardingQuestionnaireId,
            data: getMergedPatchData(values, fieldNames),
            getAccessTokenSilently,
          }),
        );

        if (
          !onboardingQuestionnaireActions.updateOnboardingQuestionnaireGeneralInfo.fulfilled.match(
            resultAction,
          )
        ) {
          return;
        }

        const errors = await formik.validateForm(values);

        formik.setErrors(errors);
        formik.setTouched(getAllFieldsTouched(values), false);
        savedFieldValuesRef.current = {
          ...savedFieldValuesRef.current,
          ...changedSavedValues,
        };
      })();

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [dispatch, formik, getAccessTokenSilently, onboardingQuestionnaireId],
  );

  const saveFieldOnBlur = useCallback(
    async (name: GeneralInformationFieldName, value?: GeneralInformationFieldValue) => {
      const values = getValuesWithFieldOverride(formik.values, name, value);

      if (!isDateFieldValid(name, values)) {
        return;
      }

      if (!(await isFieldSchemaValid(name, values))) {
        return;
      }

      await performGeneralInfoSave(values);
    },
    [formik, performGeneralInfoSave],
  );

  const saveGeneralInfoFields = useCallback(
    async (overrides: GeneralInformationFieldOverride[]) => {
      const values = applyGeneralInfoFieldOverrides(formik.values, overrides);

      for (const { name } of overrides) {
        if (!isDateFieldValid(name, values)) {
          return;
        }

        if (!(await isFieldSchemaValid(name, values))) {
          return;
        }
      }

      await performGeneralInfoSave(values);
    },
    [formik, performGeneralInfoSave],
  );

  return useMemo(
    () => ({ saveFieldOnBlur, saveGeneralInfoFields }),
    [saveFieldOnBlur, saveGeneralInfoFields],
  );
}
